import { useState, useCallback } from "react";
import { Sparkles, RotateCw, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";

const PRIZES = [
  { label: "10% OFF", color: "#C65D47", textColor: "#fff", icon: "%" },
  { label: "PERDU", color: "#1D3557", textColor: "#fff", icon: "✗" },
  { label: "5€", color: "#8C9D78", textColor: "#fff", icon: "€" },
  { label: "PERDU", color: "#2A4A6D", textColor: "#fff", icon: "✗" },
  { label: "20% OFF", color: "#C65D47", textColor: "#fff", icon: "%" },
  { label: "PERDU", color: "#1D3557", textColor: "#fff", icon: "✗" },
  { label: "CADEAU", color: "#8C9D78", textColor: "#fff", icon: "★" },
  { label: "PERDU", color: "#2A4A6D", textColor: "#fff", icon: "✗" },
];

const Confetti = () => {
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 1.5 + Math.random() * 2,
    color: ["#C65D47", "#8C9D78", "#1D3557", "#FFD700", "#FF6B6B"][Math.floor(Math.random() * 5)],
    size: 4 + Math.random() * 8,
    rotation: Math.random() * 360,
  }));

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute animate-confetti-fall"
          style={{
            left: `${p.x}%`,
            top: "-10px",
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        >
          <div
            style={{
              width: p.size,
              height: p.size * 0.6,
              backgroundColor: p.color,
              transform: `rotate(${p.rotation}deg)`,
              borderRadius: "2px",
            }}
          />
        </div>
      ))}
    </div>
  );
};

const WheelOfFortune = () => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const segmentAngle = 360 / PRIZES.length;

  const spin = useCallback(() => {
    if (isSpinning) return;
    setIsSpinning(true);
    setResult(null);
    setShowConfetti(false);

    const extraSpins = 360 * (5 + Math.floor(Math.random() * 3));
    const targetAngle = Math.floor(Math.random() * 360);
    const newRotation = rotation + extraSpins + targetAngle;
    setRotation(newRotation);

    setTimeout(() => {
      const normalizedAngle = newRotation % 360;
      const prizeIndex = Math.floor(((360 - normalizedAngle + segmentAngle / 2) % 360) / segmentAngle) % PRIZES.length;
      const won = PRIZES[prizeIndex];
      setResult(won);
      setIsSpinning(false);
      if (won.label !== "PERDU") {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
    }, 5000);
  }, [isSpinning, rotation, segmentAngle]);

  const wheelSize = 280;
  const center = wheelSize / 2;
  const radius = center - 4;

  const createSegmentPath = (index) => {
    const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
    const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);
    const x1 = center + radius * Math.cos(startAngle);
    const y1 = center + radius * Math.sin(startAngle);
    const x2 = center + radius * Math.cos(endAngle);
    const y2 = center + radius * Math.sin(endAngle);
    return `M${center},${center} L${x1},${y1} A${radius},${radius} 0 0,1 ${x2},${y2} Z`;
  };

  const getTextPosition = (index) => {
    const angle = ((index + 0.5) * segmentAngle - 90) * (Math.PI / 180);
    const textRadius = radius * 0.65;
    return {
      x: center + textRadius * Math.cos(angle),
      y: center + textRadius * Math.sin(angle),
      rotation: (index + 0.5) * segmentAngle,
    };
  };

  return (
    <div className="text-center relative" data-testid="wheel-of-fortune">
      {showConfetti && <Confetti />}

      <div className="relative inline-block mb-6">
        {/* Pointer */}
        <div className="absolute top-[-8px] left-1/2 -translate-x-1/2 z-10">
          <svg width="30" height="36" viewBox="0 0 30 36">
            <polygon points="15,36 0,0 30,0" fill="#1D3557" />
            <polygon points="15,30 4,4 26,4" fill="#C65D47" />
          </svg>
        </div>

        {/* Outer ring glow */}
        <div
          className="absolute inset-[-12px] rounded-full"
          style={{
            background: "conic-gradient(from 0deg, #C65D47, #8C9D78, #1D3557, #C65D47)",
            opacity: isSpinning ? 0.8 : 0.4,
            transition: "opacity 0.5s",
            filter: "blur(6px)",
          }}
        />

        {/* Wheel SVG */}
        <svg
          width={wheelSize}
          height={wheelSize}
          className="relative z-[2] drop-shadow-2xl"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: isSpinning ? "transform 5s cubic-bezier(0.17, 0.67, 0.05, 0.99)" : "none",
          }}
        >
          {/* Outer border */}
          <circle cx={center} cy={center} r={radius + 2} fill="#1D3557" />

          {/* Segments */}
          {PRIZES.map((prize, i) => (
            <path key={i} d={createSegmentPath(i)} fill={prize.color} stroke="#FDFBF7" strokeWidth="1.5" />
          ))}

          {/* Segment divider dots */}
          {PRIZES.map((_, i) => {
            const angle = (i * segmentAngle - 90) * (Math.PI / 180);
            const dotR = radius - 8;
            return (
              <circle
                key={`dot-${i}`}
                cx={center + dotR * Math.cos(angle)}
                cy={center + dotR * Math.sin(angle)}
                r="3"
                fill="#FFD700"
                opacity="0.7"
              />
            );
          })}

          {/* Text labels */}
          {PRIZES.map((prize, i) => {
            const pos = getTextPosition(i);
            return (
              <text
                key={`text-${i}`}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={prize.textColor}
                fontSize="13"
                fontWeight="bold"
                transform={`rotate(${pos.rotation}, ${pos.x}, ${pos.y})`}
              >
                {prize.label}
              </text>
            );
          })}

          {/* Center circle */}
          <circle cx={center} cy={center} r="28" fill="#FDFBF7" stroke="#1D3557" strokeWidth="3" />
          <circle cx={center} cy={center} r="20" fill="#1D3557" />
          <text x={center} y={center + 1} textAnchor="middle" dominantBaseline="middle" fill="#FFD700" fontSize="16" fontWeight="bold">
            GO
          </text>
        </svg>
      </div>

      {/* Result */}
      {result && (
        <div
          className={`mb-5 p-5 rounded-2xl border-2 transition-all animate-in fade-in slide-in-from-bottom-4 duration-500 ${
            result.label === "PERDU"
              ? "bg-stone-50 border-stone-200"
              : "bg-gradient-to-r from-[#8C9D78]/10 to-[#C65D47]/10 border-[#8C9D78]/30"
          }`}
          data-testid="wheel-result"
        >
          {result.label === "PERDU" ? (
            <p className="font-bold text-lg text-stone-500">Pas de chance ! Retentez votre chance !</p>
          ) : (
            <div className="flex items-center justify-center gap-3">
              <PartyPopper className="w-6 h-6 text-[#C65D47]" />
              <p className="font-bold text-xl text-[#1D3557]">
                Bravo ! Vous remportez : <span className="text-[#C65D47]">{result.label}</span>
              </p>
            </div>
          )}
        </div>
      )}

      <Button
        onClick={spin}
        disabled={isSpinning}
        className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full px-10 h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105 disabled:hover:scale-100"
        data-testid="spin-wheel-btn"
      >
        {isSpinning ? (
          <>
            <RotateCw className="w-5 h-5 mr-2 animate-spin" />
            La roue tourne...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5 mr-2" />
            Tourner la roue
          </>
        )}
      </Button>
    </div>
  );
};

export default WheelOfFortune;
